import { useContext, useEffect, useState } from 'react'
import moment from 'moment-timezone'

import { Divider, Typography, List, ListItem } from '@mui/material'

import { AppContext } from '/providers/app-context'
import { getFreeCreditsDetails } from '/providers/clients-api'


const CreditBalance = ({ balance, lastPurchase, topupBundle }) => {
  const { apiSource, apiKey } = useContext(AppContext)
  const [nextFreeCreditDate, setNextFreeCreditDate] = useState(null)

  useEffect(() => {
    let active = true
    getFreeCreditsDetails({ apiSource, apiKey }).then(result => {
      if (active && result?.ok) setNextFreeCreditDate(result.topupDate)
    })
    return () => { active = false }
  }, [apiSource, apiKey])

  const formatPrice = price => (Number(price) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  const freeCreditsRemaining = Number(balance?.free_credits ?? 0)
  const paidCreditsRemaining = Number(balance?.credits ?? 0)
  const overdraftLimit = Number(balance?.overdraft ?? 0)
  const availableWithOverdraft = Number(balance?.available_with_overdraft ?? (freeCreditsRemaining + paidCreditsRemaining + overdraftLimit))
  const overdraftRemaining = Math.max(0, availableWithOverdraft - freeCreditsRemaining - paidCreditsRemaining)

  return (
    <>
      <div className='flex flex-row p-5'>
          <div className='flex flex-col gap-5 p-3 items-center w-full'>

            <Typography variant='h4'>Credit balance</Typography>

            <List sx={{width:400}}>
              {balance && <>
              <Item title="free credits remaining" value={parseInt(balance.free_credits ?? 0,10).toLocaleString()}/>
              <Item title="next free credits" value={nextFreeCreditDate ?? "-"}/>
              <Item title="paid credits remaining" value={<span className='text-4xl'>{parseInt(balance.credits,10).toLocaleString()}</span>}/>
              <Item title="total available credits" value={(Number(balance.free_credits ?? 0) + Number(balance.credits ?? 0)).toLocaleString()}/>
              <Item title="overdraft limit" value={overdraftLimit.toLocaleString()}/>
              <Item title="overdraft remaining" value={overdraftRemaining.toLocaleString()}/>
              <Item title="total with overdraft" value={(freeCreditsRemaining + paidCreditsRemaining + overdraftLimit).toLocaleString()}/>
              </>}

              {lastPurchase && <>
              <Divider/>

              <Item title="last purchased bundle" value={lastPurchase ? lastPurchase.bundle_name : "-"}/>
              <Item title="purchased" value={moment.utc(lastPurchase.purchase_date).tz('Europe/London').format("DD MMM YYYY")}/>
              <Item title="credits puchased" value={parseInt(lastPurchase.credits,10).toLocaleString()}/>
              <Item title="price" value={"£" + parseInt(lastPurchase.price,10).toLocaleString() + (lastPurchase.vat ? ` (plus £${lastPurchase.vat} vat)` : '')}/>
              <Item title="expires" value={moment.utc(lastPurchase.purchase_date).tz('Europe/London').add(1,"year").format("DD MMM YYYY")}/>
              </>}

              {balance && <>
              <Divider/>
              <Item title="auto top-up" value={balance.auto_topup ? "Enabled" : "Disabled"} />
              <Item
                title="auto top-up bundle"
                value={topupBundle
                  ? <>
                      {topupBundle.bundle_name}<br/>
                      {parseInt(topupBundle.credits,10).toLocaleString()} credits<br/>
                      price <span className="line-through">£{formatPrice(topupBundle.price)}</span> £{formatPrice(topupBundle.auto_price)}
                    </>
                  : "-"
                }
              />
              <Item
                title={balance.auto_topup ? "auto topup threshold" : "email notification threshold"}
                value={balance.threshold ? `${balance.threshold}% remaining` : "-"}
              />
              </>}
            </List>
          </div>
      </div>
    </>
  )
}

export default CreditBalance

const Item = ({ title, value }) => (
  <ListItem>
    <div className='flex flex-row w-full justify-between'>
      <div className='capitalize text-sm'>{title}</div>
      <div className='font-bold'>{value}</div>
    </div>
  </ListItem>
)
